/**
 * Google Ads conversion attribution helpers (client-side only).
 *
 * Flow:
 *  1. captureGoogleAdsParams() is called on every page load / route change
 *     in _app.js. If any tracked params are present, it hits /api/capture-gads
 *     which encrypts them server-side and stores the result in the `fa_gads`
 *     cookie via Set-Cookie (immune to Safari ITP).
 *
 *  2. getGoogleAdsParams() is called inside checkoutClicked() to read the
 *     opaque encrypted blob from the cookie. It is forwarded as-is in the
 *     submitOrder payload for the backend to decrypt.
 */

// The URL parameters we watch for on landing pages.
// Extending this list requires no changes to the capture API route.
export const GADS_TRACKED_PARAMS = [
  'gclid',
  'gbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const GADS_COOKIE_NAME = 'fa_gads';

/**
 * Checks whether any Google Ads / UTM parameters are present in the given
 * Next.js router query object and, if so, fires a request to /api/capture-gads
 * so the params are encrypted and persisted as a server-set cookie.
 *
 * This is intentionally fire-and-forget – errors are swallowed so a tracking
 * failure never blocks the user's session.
 *
 * @param {import('next/router').NextRouter['query']} query
 */
export function captureGoogleAdsParams(query) {
  if (typeof window === 'undefined') return;

  const present = GADS_TRACKED_PARAMS.filter((p) => Boolean(query[p]));
  if (present.length === 0) return;

  // Build query string with only the tracked, present params
  const params = new URLSearchParams();
  for (const p of present) {
    params.set(p, query[p]);
  }

  // Fire-and-forget – swallow errors gracefully
  fetch(`/api/capture-gads?${params.toString()}`).catch(() => {});
}

/**
 * Reads the encrypted Google Ads attribution blob from the `fa_gads` cookie.
 *
 * Returns the raw encrypted string as-is – decryption happens exclusively on
 * the backend using the shared GADS_ENCRYPTION_KEY.
 *
 * @returns {{ encryptedBlob: string | null }}
 */
export function getGoogleAdsParams() {
  if (typeof window === 'undefined') return { encryptedBlob: null };

  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${GADS_COOKIE_NAME}=`));

  const encryptedBlob = match ? match.split('=').slice(1).join('=') : null;
  return { encryptedBlob };
}
